import React, { useState, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Truck, Shield, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppFloat from "../components/WhatsAppFloat";
import { BODY_MODELS, COLORS, SIZES, buildWhatsAppLink } from "../constants";

export default function BodyDetailPage() {
  const { slug } = useParams();
  const model = useMemo(
    () => BODY_MODELS.find((m) => m.slug === slug),
    [slug]
  );

  const [activeImage, setActiveImage] = useState(model?.gallery?.[0] || model?.image);
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState("M");

  if (!model) return <Navigate to="/bodys" replace />;

  const gallery = model.gallery && model.gallery.length > 0 ? model.gallery : [model.image];

  const waMessage = `Hola Vallitas, quiero pedir el body modelo ${model.name} en color ${color.name}, talla ${size}.`;

  // Recommend related models
  const related = BODY_MODELS.filter((m) => m.slug !== slug).slice(0, 3);

  return (
    <main
      data-testid={`body-detail-${slug}`}
      className="min-h-screen bg-page text-ink"
    >
      <Navbar />

      {/* Detail */}
      <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          {/* Breadcrumbs */}
          <nav
            data-testid="detail-breadcrumb"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft mb-10"
          >
            <Link to="/" className="hover:text-vino transition-colors">
              Inicio
            </Link>
            <span>/</span>
            <Link to="/bodys" className="hover:text-vino transition-colors">
              Bodys
            </Link>
            <span>/</span>
            <span className="text-ink">{model.name}</span>
          </nav>

          <Link
            to="/bodys"
            data-testid="detail-back-link"
            className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-vino transition-colors mb-8"
          >
            <ArrowLeft size={16} /> Volver a la colección
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Gallery */}
            <div className="lg:col-span-7 lg:sticky lg:top-28 self-start">
              <div
                key={activeImage}
                className="img-zoom rounded-[2rem] overflow-hidden aspect-[4/5] sm:aspect-[5/6] shadow-[0_50px_120px_-30px_rgba(44,36,36,0.45)] fade-up"
              >
                <img
                  src={activeImage}
                  alt={`Body Vallitas ${model.name}`}
                  className="w-full h-full object-cover"
                  data-testid="detail-main-image"
                />
              </div>

              {gallery.length > 1 && (
                <div
                  data-testid="detail-thumbnails"
                  className="mt-5 grid grid-cols-4 gap-3 sm:gap-4"
                >
                  {gallery.map((src, i) => {
                    const isActive = src === activeImage;
                    return (
                      <button
                        key={src}
                        onClick={() => setActiveImage(src)}
                        data-testid={`thumbnail-${i}`}
                        aria-label={`Ver imagen ${i + 1} del modelo ${model.name}`}
                        className={`relative rounded-2xl overflow-hidden aspect-square transition-all ${
                          isActive
                            ? "ring-2 ring-vino ring-offset-2 ring-offset-page"
                            : "ring-1 ring-line hover:ring-vino/50 opacity-80 hover:opacity-100"
                        }`}
                      >
                        <img
                          src={src}
                          alt={`${model.name} vista ${i + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-vino/30 bg-page px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-vino font-medium mb-5">
                <Sparkles size={11} /> Body Vallitas
              </div>

              <h1
                data-testid="detail-title"
                className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-ink"
              >
                {model.name}
              </h1>
              <p className="mt-3 font-heading italic text-2xl text-vino">
                {model.tagline}
              </p>
              <p className="mt-6 text-base text-ink-soft leading-relaxed">
                {model.longDescription}
              </p>

              {/* Color */}
              <div className="mt-10">
                <div className="flex items-baseline justify-between mb-4">
                  <h3 className="text-[11px] uppercase tracking-[0.25em] text-ink font-medium">
                    Color
                  </h3>
                  <span
                    data-testid="selected-color-name"
                    className="text-sm text-ink-soft"
                  >
                    {color.name}
                  </span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {COLORS.map((c) => {
                    const isActive = c.slug === color.slug;
                    const isLight = ["blanco", "beige", "rosa-palo", "gris-perla"].includes(c.slug);
                    return (
                      <button
                        key={c.slug}
                        onClick={() => setColor(c)}
                        aria-label={`Color ${c.name}`}
                        data-testid={`color-${c.slug}`}
                        className={`relative w-11 h-11 rounded-full transition-all ${
                          isActive
                            ? "ring-2 ring-vino ring-offset-2 ring-offset-page scale-105"
                            : "ring-1 ring-line hover:scale-105"
                        }`}
                        style={{ backgroundColor: c.hex }}
                      >
                        {isActive && (
                          <Check
                            size={16}
                            className={isLight ? "text-ink mx-auto" : "text-white mx-auto"}
                            strokeWidth={2.5}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Size */}
              <div className="mt-10">
                <div className="flex items-baseline justify-between mb-4">
                  <h3 className="text-[11px] uppercase tracking-[0.25em] text-ink font-medium">
                    Talla
                  </h3>
                  <a
                    href={buildWhatsAppLink("Hola, quiero la guía de tallas de los bodys.")}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="size-guide-link"
                    className="text-sm text-vino link-underline"
                  >
                    Guía de tallas
                  </a>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {SIZES.map((s) => {
                    const isActive = s === size;
                    return (
                      <button
                        key={s}
                        onClick={() => setSize(s)}
                        data-testid={`size-${s.toLowerCase()}`}
                        className={`min-w-[56px] px-4 py-3 rounded-full text-sm font-medium border transition-all ${
                          isActive
                            ? "bg-vino text-page border-vino"
                            : "bg-transparent text-ink border-line hover:border-vino"
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-xs text-ink-soft">{model.fit}</p>
              </div>

              {/* Fabric/Care */}
              <div className="mt-10 grid sm:grid-cols-2 gap-4">
                <div className="paper-card rounded-2xl p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-vino mb-2 font-medium">
                    Tela
                  </div>
                  <p className="text-sm text-ink leading-snug">{model.fabric}</p>
                </div>
                <div className="paper-card rounded-2xl p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-vino mb-2 font-medium">
                    Cuidados
                  </div>
                  <p className="text-sm text-ink leading-snug">{model.care}</p>
                </div>
              </div>

              {/* CTA */}
              <a
                href={buildWhatsAppLink(waMessage)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-whatsapp-cta"
                className="mt-10 group inline-flex w-full items-center justify-center gap-2 rounded-full bg-vino text-page px-8 py-5 text-base font-medium hover:bg-vino-deep transition-all hover:-translate-y-1 shadow-[0_24px_60px_-15px_rgba(114,47,55,0.6)]"
              >
                Pedir {model.name} · {color.name} · Talla {size}
                <ArrowUpRight
                  size={18}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>

              <p className="mt-4 text-xs text-center text-ink-soft">
                Confección desde $10 · Te respondemos en minutos por WhatsApp
              </p>

              {/* Trust badges */}
              <div className="mt-8 grid grid-cols-3 gap-3 pt-8 border-t border-line">
                <div className="flex flex-col items-center text-center gap-2">
                  <Truck size={20} className="text-vino" />
                  <span className="text-[11px] uppercase tracking-[0.18em] text-ink-soft">
                    Envío nacional
                  </span>
                </div>
                <div className="flex flex-col items-center text-center gap-2">
                  <Shield size={20} className="text-vino" />
                  <span className="text-[11px] uppercase tracking-[0.18em] text-ink-soft">
                    Calidad garantizada
                  </span>
                </div>
                <div className="flex flex-col items-center text-center gap-2">
                  <Sparkles size={20} className="text-vino" />
                  <span className="text-[11px] uppercase tracking-[0.18em] text-ink-soft">
                    Personalizable
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related models */}
      <section
        data-testid="related-models"
        className="relative py-20 sm:py-28 bg-sand"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-ink leading-tight">
              También te enamorarás de
            </h2>
            <Link
              to="/bodys"
              className="text-sm text-vino link-underline self-start sm:self-auto"
            >
              Ver toda la colección →
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {related.map((m) => (
              <Link
                key={m.slug}
                to={`/bodys/${m.slug}`}
                data-testid={`related-${m.slug}`}
                onClick={() => setActiveImage(m.image)}
                className="group block"
              >
                <div className="img-zoom rounded-[2rem] overflow-hidden aspect-[4/5]">
                  <img
                    src={m.image}
                    alt={m.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="mt-5 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-heading text-2xl sm:text-3xl text-ink">
                      {m.name}
                    </h3>
                    <p className="text-sm italic text-vino mt-1">{m.tagline}</p>
                  </div>
                  <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full border border-vino/40 text-vino group-hover:bg-vino group-hover:text-page transition-colors">
                    <ArrowUpRight size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </main>
  );
}
